// Aggregation logic for the `salesMetrics/{YYYY-MM-DD}` collection.
//
// Phase 2 of the sales analytics build. Daily docs are recomputed whenever a
// lead (an `estimates` doc) is written. Storing one small doc per day means
// dashboards can read 365 docs instead of scanning thousands of leads.
//
// This module is dependency-free so it can be loaded from both browser code
// (AppContext hooks) and Netlify functions (recompute, backfill).

// ===== Date helpers =====

// Convert an ISO timestamp (or Date) to a UTC YYYY-MM-DD string.
// We use UTC consistently because every timestamp written by Phase 1 came from
// `new Date().toISOString()`. Mixing UTC and local-time day boundaries here
// would silently misattribute writes that land near midnight.
export function isoDayKey(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    // Fast path: ISO strings start with YYYY-MM-DD.
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return value.toISOString().slice(0, 10);
  }
  // Firestore Timestamp objects expose a `seconds` field via REST conversion.
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000).toISOString().slice(0, 10);
  }
  return null;
}

// Inclusive list of YYYY-MM-DD strings from `start` to `end` (both UTC).
export function listDayKeys(startDayKey, endDayKey) {
  const out = [];
  const start = new Date(`${startDayKey}T00:00:00Z`);
  const end = new Date(`${endDayKey}T00:00:00Z`);
  for (let d = start; d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

// The set of day keys that a single lead can affect: its creation day plus
// every stageHistory entry's day. Used by the onWrite trigger to know which
// salesMetrics docs to recompute.
export function affectedDayKeysForLead(lead) {
  const days = new Set();
  const created = isoDayKey(lead?.createdAt);
  if (created) days.add(created);
  const history = Array.isArray(lead?.stageHistory) ? lead.stageHistory : [];
  for (const entry of history) {
    const key = isoDayKey(entry?.enteredAt);
    if (key) days.add(key);
  }
  return [...days];
}

// ===== Field extraction =====

// Stages we explicitly bucket. Anything else falls under `other`.
const KNOWN_STAGES = new Set([
  'new', 'contacted', 'walkthrough_scheduled', 'estimate_in_progress',
  'estimate_sent', 'follow_up', 'won', 'lost',
]);

function safeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

// Best-effort quote amount for a lead. Mirrors the candidates in
// leadStageHistory.deriveQuoteAmount but inlined to keep this module
// dependency-free for the Netlify side.
function readQuoteAmount(lead) {
  const candidates = [
    lead?.quoteAmount, lead?.total, lead?.totalPrice,
    lead?.approvedTotal, lead?.approvedRecurringTotal, lead?.approvedDeepCleanTotal,
    lead?.recurringTotal, lead?.deepCleanTotal, lead?.basePrice, lead?.price,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

function readWonValue(lead) {
  const direct = Number(lead?.finalJobValue);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const approved = Number(lead?.approvedTotal);
  if (Number.isFinite(approved) && approved > 0) return approved;
  const split = (Number(lead?.approvedRecurringTotal) || 0) + (Number(lead?.approvedDeepCleanTotal) || 0);
  if (split > 0) return split;
  return readQuoteAmount(lead);
}

function dimKey(value) {
  if (value == null || value === '') return 'unknown';
  return String(value);
}

// ===== Aggregation =====

// Empty per-dimension breakdown.
function emptyBreakdown() {
  return { newLeads: 0, quotesSent: 0, won: 0, lost: 0, quotesSentValue: 0, wonValue: 0 };
}

// Empty daily metrics doc.
export function emptyDailyMetrics(dayKey) {
  return {
    date: dayKey,
    newLeads: 0,
    quotesSent: 0,
    won: 0,
    lost: 0,
    quotesSentValue: 0,
    wonValue: 0,
    bySource: {},
    byServiceType: {},
    byStage: {},
  };
}

function bumpDim(map, dimValue, field, increment = 1) {
  if (!increment) return;
  const key = dimKey(dimValue);
  if (!map[key]) map[key] = emptyBreakdown();
  map[key][field] = (map[key][field] || 0) + increment;
}

// Compute a single daily salesMetrics doc from a list of leads (estimates).
// Caller is responsible for narrowing the lead list to records that may have
// touched this day; passing the full collection is fine but wasteful for
// the onWrite path.
//
// Daily contributions:
//   - `newLeads`         : leads with createdAt on this day
//   - `quotesSent`       : stage transitions into `estimate_sent` on this day
//   - `won` / `lost`     : stage transitions into `won` / `lost` on this day
//   - `quotesSentValue`  : sum of quoteAmount for those quotesSent transitions
//   - `wonValue`         : sum of finalJobValue (or fallback) for won transitions
//   - `bySource[s]`      : per-source breakdown of all the above (newLeads keyed
//                          to lead.source; transitions also keyed to lead.source)
//   - `byServiceType[s]` : same shape, keyed by lead.serviceType
//   - `byStage[s]`       : count of stage transitions into stage `s` on this day
//                          (covers every stage, not just won/lost/sent)
export function computeDailyMetrics(dayKey, leads) {
  const out = emptyDailyMetrics(dayKey);
  if (!Array.isArray(leads) || leads.length === 0) return out;

  for (const lead of leads) {
    const source = dimKey(lead?.source);
    const serviceType = dimKey(lead?.serviceType);

    // ---- newLeads (lead created on this day) ----
    if (isoDayKey(lead?.createdAt) === dayKey) {
      out.newLeads += 1;
      bumpDim(out.bySource, source, 'newLeads');
      bumpDim(out.byServiceType, serviceType, 'newLeads');
    }

    // ---- stage transitions on this day ----
    const history = Array.isArray(lead?.stageHistory) ? lead.stageHistory : [];
    for (const entry of history) {
      if (isoDayKey(entry?.enteredAt) !== dayKey) continue;
      const stage = entry?.stage || 'unknown';
      const stageBucket = KNOWN_STAGES.has(stage) ? stage : 'other';
      out.byStage[stageBucket] = (out.byStage[stageBucket] || 0) + 1;

      if (stage === 'estimate_sent') {
        const v = readQuoteAmount(lead);
        out.quotesSent += 1;
        out.quotesSentValue += v;
        bumpDim(out.bySource, source, 'quotesSent');
        bumpDim(out.bySource, source, 'quotesSentValue', v);
        bumpDim(out.byServiceType, serviceType, 'quotesSent');
        bumpDim(out.byServiceType, serviceType, 'quotesSentValue', v);
      } else if (stage === 'won') {
        const v = readWonValue(lead);
        out.won += 1;
        out.wonValue += v;
        bumpDim(out.bySource, source, 'won');
        bumpDim(out.bySource, source, 'wonValue', v);
        bumpDim(out.byServiceType, serviceType, 'won');
        bumpDim(out.byServiceType, serviceType, 'wonValue', v);
      } else if (stage === 'lost') {
        out.lost += 1;
        bumpDim(out.bySource, source, 'lost');
        bumpDim(out.byServiceType, serviceType, 'lost');
      }
    }
  }

  // Round monetary totals to 2 decimal places to keep stored doc sizes small
  // and avoid floating-point noise leaking into the dashboard.
  out.quotesSentValue = Math.round(out.quotesSentValue * 100) / 100;
  out.wonValue = Math.round(out.wonValue * 100) / 100;
  for (const dim of [out.bySource, out.byServiceType]) {
    for (const k of Object.keys(dim)) {
      dim[k].quotesSentValue = Math.round((dim[k].quotesSentValue || 0) * 100) / 100;
      dim[k].wonValue = Math.round((dim[k].wonValue || 0) * 100) / 100;
    }
  }
  return out;
}

// Compute a map of `{ [YYYY-MM-DD]: dailyMetrics }` for every day in the
// inclusive range. Single pass over leads.
export function computeMetricsForRange(startDayKey, endDayKey, leads) {
  const dayKeys = listDayKeys(startDayKey, endDayKey);
  const result = {};
  for (const k of dayKeys) result[k] = emptyDailyMetrics(k);
  if (!Array.isArray(leads) || leads.length === 0) return result;

  for (const lead of leads) {
    const created = isoDayKey(lead?.createdAt);
    const source = dimKey(lead?.source);
    const serviceType = dimKey(lead?.serviceType);

    if (created && result[created]) {
      const out = result[created];
      out.newLeads += 1;
      bumpDim(out.bySource, source, 'newLeads');
      bumpDim(out.byServiceType, serviceType, 'newLeads');
    }

    const history = Array.isArray(lead?.stageHistory) ? lead.stageHistory : [];
    for (const entry of history) {
      const day = isoDayKey(entry?.enteredAt);
      if (!day || !result[day]) continue;
      const out = result[day];
      const stage = entry?.stage || 'unknown';
      const stageBucket = KNOWN_STAGES.has(stage) ? stage : 'other';
      out.byStage[stageBucket] = (out.byStage[stageBucket] || 0) + 1;

      if (stage === 'estimate_sent') {
        const v = readQuoteAmount(lead);
        out.quotesSent += 1;
        out.quotesSentValue += v;
        bumpDim(out.bySource, source, 'quotesSent');
        bumpDim(out.bySource, source, 'quotesSentValue', v);
        bumpDim(out.byServiceType, serviceType, 'quotesSent');
        bumpDim(out.byServiceType, serviceType, 'quotesSentValue', v);
      } else if (stage === 'won') {
        const v = readWonValue(lead);
        out.won += 1;
        out.wonValue += v;
        bumpDim(out.bySource, source, 'won');
        bumpDim(out.bySource, source, 'wonValue', v);
        bumpDim(out.byServiceType, serviceType, 'won');
        bumpDim(out.byServiceType, serviceType, 'wonValue', v);
      } else if (stage === 'lost') {
        out.lost += 1;
        bumpDim(out.bySource, source, 'lost');
        bumpDim(out.byServiceType, serviceType, 'lost');
      }
    }
  }

  // Final rounding pass.
  for (const k of Object.keys(result)) {
    const out = result[k];
    out.quotesSentValue = Math.round(out.quotesSentValue * 100) / 100;
    out.wonValue = Math.round(out.wonValue * 100) / 100;
    for (const dim of [out.bySource, out.byServiceType]) {
      for (const dimKeyName of Object.keys(dim)) {
        dim[dimKeyName].quotesSentValue = Math.round((dim[dimKeyName].quotesSentValue || 0) * 100) / 100;
        dim[dimKeyName].wonValue = Math.round((dim[dimKeyName].wonValue || 0) * 100) / 100;
      }
    }
  }
  return result;
}

// Return the list of day keys for the trailing 12 months ending today (UTC).
export function trailing12MonthsDayKeys(today = new Date()) {
  const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const start = new Date(end);
  start.setUTCFullYear(start.getUTCFullYear() - 1);
  start.setUTCDate(start.getUTCDate() + 1); // +1 so the range is exactly 365/366 days, not 366/367
  return listDayKeys(start.toISOString().slice(0, 10), end.toISOString().slice(0, 10));
}
